'use client'

import { useRef, useState } from 'react'
import { useRouter } from '@bprogress/next/app'
import { useTranslations } from 'next-intl'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { CategoryForm, type CategoryFormRef } from '@/components/partials/categories'
import { useCreateCategory } from '@/hooks'
import { CreateCategoryParams } from '@/types/category'
import { FormModeEnum } from '@/enums'

const Create = () => {
  const t = useTranslations()
  const router = useRouter()
  const queryClient = useQueryClient()
  const categoryFormRef = useRef<CategoryFormRef>(null)
  const [isRedirecting, setIsRedirecting] = useState(false)
  const [formKey, setFormKey] = useState(0)
  const { mutate: createCategory, isPending: isLoading } = useCreateCategory()

  const handleSubmit = (data: CreateCategoryParams) => {
    createCategory(data, {
      onSuccess: async newCategory => {
        setIsRedirecting(true)
        queryClient.setQueryData(['category', newCategory.id], newCategory)
        await queryClient.invalidateQueries({ queryKey: ['categories'] })
        await queryClient.invalidateQueries({ queryKey: ['categories-tree'] })
        toast.success(t('created_successfully'))
        router.push(`/products/categories/${newCategory.id}`)
      },
      onError: handleError
    })
  }

  const handleSubmitAndNew = (data: CreateCategoryParams) => {
    createCategory(data, {
      onSuccess: async newCategory => {
        queryClient.setQueryData(['category', newCategory.id], newCategory)
        await queryClient.invalidateQueries({ queryKey: ['categories'] })
        await queryClient.invalidateQueries({ queryKey: ['categories-tree'] })
        const detailUrl = `/products/categories/${newCategory.id}`
        toast.success(t('created_successfully'), {
          action: {
            label: t('view'),
            onClick: () => router.push(detailUrl)
          }
        })
        categoryFormRef.current?.reset()
        setFormKey(prev => prev + 1)
      },
      onError: handleError
    })
  }

  const handleError = (error: any) => {
    if (error.response?.status === 422 || error.isValidationError) {
      if (categoryFormRef.current) {
        categoryFormRef.current.handleValidationErrors(error)
      }
      return
    }

    toast.error(t('error_creating'))
  }

  const handleCancel = () => {
    router.push('/products/categories')
  }

  return (
    <CategoryForm
      key={formKey}
      ref={categoryFormRef}
      mode={FormModeEnum.CREATE}
      onSubmit={handleSubmit}
      onSubmitAndNew={handleSubmitAndNew}
      onCancel={handleCancel}
      isLoading={isLoading || isRedirecting}
    />
  )
}

export default Create
